import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useBranding } from '../TournamentBrandingContext';
import {
  Box, Container, Typography, Card, CardContent, Button,
  CircularProgress, Alert, Table, TableBody, TableRow, TableCell, Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HomeIcon from '@mui/icons-material/Home';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';

export default function TournamentSuccess() {
  const [params] = useSearchParams();
  const regId = params.get('reg_id');
  const navigate = useNavigate();
  const b = useBranding();

  const [reg, setReg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [freeClinics, setFreeClinics] = useState([]); // clinics they're eligible for but not yet registered

  useEffect(() => {
    async function load() {
      if (!regId) { setLoading(false); return; }
      try {
        // Load the registration
        const snap = await getDoc(doc(db, 'registrations', regId));
        if (!snap.exists()) { setLoading(false); return; }
        const regData = { id: snap.id, ...snap.data() };
        setReg(regData);

        // Load the tournament to find free_if_registered clinics
        const tSnap = await getDoc(doc(db, 'tournaments', regData.tournament_id));
        if (!tSnap.exists()) return;
        const tournament = tSnap.data();
        const freeIfReg = tournament.free_if_registered || {};

        // Find all clinic divisions with free_if_registered: true
        const eligibleClinics = Object.entries(freeIfReg)
          .filter(([, val]) => val === true)
          .map(([div]) => div);

        if (eligibleClinics.length === 0) return;

        // Check which ones they haven't already registered for
        const existingSnap = await getDocs(query(
          collection(db, 'registrations'),
          where('tournament_id', '==', regData.tournament_id),
          where('email', '==', regData.email),
          where('status', '==', 'active')
        ));
        const alreadyRegisteredDivs = existingSnap.docs.map(d => d.data().division);

        const available = eligibleClinics.filter(div => !alreadyRegisteredDivs.includes(div));
        setFreeClinics(available);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, [regId]);

  if (loading) return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;

  if (!reg) return (
    <Container sx={{ textAlign: 'center', py: 8 }}>
      <Typography variant="h2" gutterBottom>🎉</Typography>
      <Typography variant="h4" fontWeight="bold" color="success.main" gutterBottom>You're Registered!</Typography>
      <Typography color="text.secondary">Your registration has been received.</Typography>
      <Button variant="contained" startIcon={<HomeIcon />} sx={{ mt: 3 }} onClick={() => navigate('/tournaments')}>
        Back to Tournaments
      </Button>
    </Container>
  );

  const isWaitlist = reg.waitlist_position > 0;
  const partnerName = reg.partner_name ||
    (reg.partner_first_name ? `${reg.partner_first_name} ${reg.partner_last_name || ''}`.trim() : null);

  const paymentChip = {
    paid:      <Chip label="Paid in Full" color="success" size="small" />,
    half_paid: <Chip label="50% Deposit Paid" color="warning" size="small" />,
    pending:   <Chip label="Pay Later" size="small" />,
  }[reg.payment_status] || <Chip label={reg.payment_status} size="small" />;

  return (
    <Container maxWidth="sm" sx={{ py: 5 }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h2" gutterBottom>🎉</Typography>
        <Typography variant="h4" fontWeight="bold" color="success.main" gutterBottom>You're Registered!</Typography>
      </Box>

      <Alert
        severity={isWaitlist ? 'warning' : 'success'}
        icon={isWaitlist ? undefined : <CheckCircleIcon />}
        sx={{ mb: 3 }}
      >
        {isWaitlist ? (
          <><strong>Waitlist Position #{reg.waitlist_position}</strong> — The Main Draw for <strong>{reg.division}</strong> is full.
          You've been added to the waitlist and will be automatically moved if a spot opens.</>
        ) : (
          <><strong>Main Draw</strong> — You are registered in the Main Draw for <strong>{reg.division}</strong>.</>
        )}
      </Alert>

      {/* Summary */}
      <Card elevation={2} sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Table size="small">
            <TableBody>
              {[
                ['Tournament', reg.tournament_name],
                ['Division', reg.division],
                ['Player 1', `${reg.first_name} ${reg.last_name}`],
                ...(partnerName ? [['Partner', partnerName]] : []),
                ['Payment', paymentChip],
              ].map(([label, value]) => (
                <TableRow key={label}>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold', border: 0, width: '40%' }}>{label}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', border: 0 }}>{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Free clinic prompt */}
      {freeClinics.length > 0 && (
        <Card elevation={2} sx={{
          borderRadius: 3, mb: 3,
          border: '2px solid', borderColor: 'info.main',
          bgcolor: 'info.50',
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <CardGiftcardIcon color="info" />
              <Typography variant="h6" fontWeight="bold" color="info.main">
                Free Clinic{freeClinics.length > 1 ? 's' : ''} Available!
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              As a registered participant in <strong>{reg.tournament_name}</strong>, you're eligible for free registration in the following clinic{freeClinics.length > 1 ? 's' : ''}:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {freeClinics.map(div => (
                <Box key={div} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fff', borderRadius: 2, px: 2, py: 1.5 }}>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">{div}</Typography>
                    <Chip label="FREE with your registration" color="success" size="small" sx={{ mt: 0.5 }} />
                  </Box>
                  <Button
                    variant="contained"
                    color="info"
                    size="small"
                    onClick={() => navigate(`/tournament/${reg.tournament_id}/${encodeURIComponent(div)}`)}
                  >
                    Register Free
                  </Button>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Payment-specific reminders */}
      {reg.payment_status === 'pending' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>Payment Reminder:</strong> You've reserved your spot but payment is still due.
          Please arrange payment with the tournament organizer before the event.
          Payments made the day of the tournament will be an additional <strong>$5 per player</strong>.
        </Alert>
      )}
      {reg.payment_status === 'half_paid' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>Balance Due:</strong> Your 50% deposit has been received. The remaining balance is due before the tournament.
          Payments made the day of the tournament will be an additional <strong>$5 per player</strong>.
          You will <strong>not appear in vb-scores</strong> until the CSV has been imported.
        </Alert>
      )}
      {reg.payment_status === 'paid' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Heads up!</strong> You will <strong>not appear in vb-scores</strong> until the tournament CSV has been
          imported by the organizer. A receipt was sent to your email.
        </Alert>
      )}

      <Button variant="contained" fullWidth size="large" startIcon={<HomeIcon />}
              sx={{ bgcolor: b.button_bg, color: b.button_text, '&:hover': { bgcolor: b.primary } }}
              onClick={() => navigate('/tournaments')}>
        Back to Tournaments
      </Button>

      {/* Cancel link */}
      {reg.cancel_token && (
        <Card variant="outlined" sx={{ mt: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Need to cancel?</strong> Save this link — it's the only way to cancel and receive a refund:
            </Typography>
            <Typography
              variant="body2" color="error"
              component="a"
              href={`${window.location.origin}/cancel/${reg.cancel_token}`}
              sx={{ wordBreak: 'break-all' }}
            >
              {window.location.origin}/cancel/{reg.cancel_token}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Container>
  );
}
